import * as echarts from 'echarts/core';
import ZrHandler from 'zrender/lib/Handler.js';
import { LineChart, BarChart, PieChart, EffectScatterChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  GraphicComponent,
  PolarComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  LineChart,
  BarChart,
  PieChart,
  EffectScatterChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  MarkLineComponent,
  GraphicComponent,
  PolarComponent,
  CanvasRenderer,
]);

// ECharts/zrender stale-series mouseout race: when `setOption` swaps in new
// series data while the cursor is hovering a data point, a later pointer event
// walks a display element whose stored `ecData` references a series whose
// `getData()` now returns undefined. zrender's `Handler.dispatchToElement` then
// crashes deep inside `findEventDispatcher → getDataParams`, which operates on
// the now-undefined series data. This surfaces as one of two TypeError
// fingerprints, depending on the series type's `getDataParams` path:
//   - Cartesian series: `data.getRawIndex(...)` →
//     "Cannot read properties of undefined (reading 'getRawIndex')"
//   - Pie series: `innerData(data)` runs a `makeInner` accessor on undefined →
//     "Cannot read properties of undefined (reading '__ec_inner_<N>')"
// Both are the same disposed/stale-series dispatch and are safe to drop.
//
// We patch the zrender `Handler` prototype ONCE at module load — before any
// chart mounts — rather than per-instance in `onChartReady`. A per-instance
// patch has lifecycle gaps: `echarts-for-react` creates a temporary instance,
// disposes it, then recreates the final instance (and re-inits on theme/opts
// changes), so the handler that actually crashes isn't always the one that got
// decorated. Patching the shared prototype covers every current and future
// instance with no timing dependency. We swallow only these specific
// stale-series TypeErrors and rethrow everything else so unrelated bugs still
// surface.
// See: https://github.com/apache/echarts/issues/19191
export function patchZrHandlerForStaleSeriesCrash(
  HandlerCtor: { prototype?: unknown } | null | undefined,
): void {
  const proto = HandlerCtor?.prototype as
    | { dispatchToElement?: unknown; __staleSeriesSafePatched?: boolean }
    | undefined;
  if (!proto || typeof proto.dispatchToElement !== 'function') return;
  if (proto.__staleSeriesSafePatched) return;
  proto.__staleSeriesSafePatched = true;

  const original = proto.dispatchToElement as (...args: unknown[]) => unknown;
  proto.dispatchToElement = function safeDispatchToElement(this: unknown, ...args: unknown[]) {
    try {
      return original.apply(this, args);
    } catch (err) {
      const message = (err as { message?: string } | undefined)?.message ?? String(err);
      if (
        err instanceof TypeError &&
        (message.includes('getRawIndex') || message.includes('__ec_inner_'))
      ) {
        // Known ECharts stale-series mouseout race; safe to drop this dispatch.
        return;
      }
      throw err;
    }
  };
}

patchZrHandlerForStaleSeriesCrash(ZrHandler);

export default echarts;
